// The crowd ladder: what the bots are allowed to spend as the roster grows.
// The host thinks for every bot 66 times a second, and a Cloudflare isolate is
// a fraction of a laptop core, so a full box has to run cheaper bots or the
// whole match runs in slow motion for everyone: a smooth match against simpler
// opponents beats a stuttering one against clever ones. Small games pay
// nothing: levels 0 and 1 are the full planner with no budgets at all. Every knob the
// planner, the board model and the host loop scale by the roster lives here,
// so the trade-off is one table, and offline and online read the same one.
//
//   level 0  1v1 (2 players) its own level, so the duel bot can be tuned (and spend
//                            CPU) on its own: a wider fan, faster replans, finer steps
//   level 1  3-8 players     full planner, no budgets (small brawls)
//   level 2  9-16 players    per-tick budgets on, slightly slower cadence
//   level 3  17-24 players   shorter lookahead, slower cadence and board refresh
//   level 4  25+ players     no personalities, shared room map only, 20ms host step
//
// A bot's rating identity is per level (bot-identity.js), so renumbering or
// moving a boundary starts those levels' ladders afresh.
//
// What each knob is worth was measured on 40 bots (mean seconds alive per bot
// over several seeds, and tick cost): the FAN is the survival lever (9 turns
// vs 4: +1.5s alive for +0.08ms), so every level keeps it; opponent prediction
// and lookahead each add ~1s for ~0.1ms; the personalities add nothing to
// survival for +0.5ms, so a crowd drops them; the 20ms step and the budgets
// cost no survival at all.
// nearTie (level 0 only): candidates within this much (x safeTime) of the best
// are a toss-up (path-checker NEAR_TIE, 0.35 elsewhere: it keeps bots from all
// driving the same arc). A duel bot takes the best outright: +14 Elo against
// the 1v1 ladder in the duel lab (6000 games, 95% +5..+23).
export var CROWD_LADDER = [
    // Level 0 spends the 1v1's spare CPU (measured in tools/bots/duel.mjs, 2026-09-30):
    // 14 extra fan angles, replanning twice as often and collision steps twice as
    // fine: +17 Elo (95% +1..+32) against the same brain on level 1's settings, for
    // ~0.06ms more per bot per tick. More lookahead made it WEAKER (x1.5: -37,
    // x2: -77), so depth stays at 1.
    { level: 0, fan: 9, fanExtra: 14, replanScale: 0.5, simDeltaScale: 0.5, nearTie: 0, lookaheadScale: 1, maxReevalsPerTick: Infinity, maxFullReplansPerTick: Infinity, personalities: true,  threats: true, ownField: true,  boardRefresh: 0.1,  tickMs: 15 },
    { level: 1, fan: 9, replanScale: 1,    lookaheadScale: 1,    maxReevalsPerTick: Infinity, maxFullReplansPerTick: Infinity, personalities: true,  threats: true, ownField: true,  boardRefresh: 0.1,  tickMs: 15 },
    { level: 2, fan: 9, replanScale: 1.15, lookaheadScale: 1,    maxReevalsPerTick: 2,        maxFullReplansPerTick: 3,        personalities: true,  threats: true, ownField: true,  boardRefresh: 0.1,  tickMs: 15 },
    { level: 3, fan: 9, replanScale: 1.3,  lookaheadScale: 0.85, maxReevalsPerTick: 2,        maxFullReplansPerTick: 3,        personalities: true,  threats: true, ownField: true,  boardRefresh: 0.16, tickMs: 15 },
    { level: 4, fan: 9, replanScale: 1.3,  lookaheadScale: 0.85, maxReevalsPerTick: 2,        maxFullReplansPerTick: 3,        personalities: false, threats: true, ownField: false, boardRefresh: 0.25, tickMs: 20 },
];

// The biggest roster each level covers (level 3 has no ceiling). The one place
// the level boundaries live: crowdLevel and the admin bot leaderboard's tabs
// both read it.
export var CROWD_LEVEL_MAX_ROSTER = [2, 8, 16, 24, Infinity];

// What people SEE: levels 1 to 5. The code, the ladder rows and the bots' rating
// ids ("...@L0" is the 1v1 level) count from 0; every screen shows this number
// instead, so nobody has to talk about "level 0".
export function crowdLevelNumber(level) {
    return level + 1;
}

export function crowdLevel(roster) {
    var n = roster || 0;
    for (var level = 0; level < CROWD_LEVEL_MAX_ROSTER.length - 1; level++) {
        if (n <= CROWD_LEVEL_MAX_ROSTER[level]) { return level; }
    }
    return CROWD_LEVEL_MAX_ROSTER.length - 1;
}

// The rosters a level covers, as { min, max } (max is Infinity for the last).
export function crowdLevelRosters(level) {
    return { min: level > 0 ? CROWD_LEVEL_MAX_ROSTER[level - 1] + 1 : 1, max: CROWD_LEVEL_MAX_ROSTER[level] };
}

export function crowdSettings(roster) {
    return CROWD_LADDER[crowdLevel(roster)];
}

// The level a match runs at. A match can carry its level explicitly
// (`crowdLevel` on the matchConfig / gameState): the host decides it from the
// BOX's size when the box opens, before anyone is seated, so the engine, the
// seating (bot-cast.js) and the bots' rating identities all read one number
// that never depends on how the box happened to fill. Without one (offline
// play, old replays) the roster decides. An explicit level can only make a
// match cheaper than its roster demands, never dearer: the ladder exists so the
// host keeps pace.
export function resolveCrowdLevel(explicitLevel, roster) {
    var byRoster = crowdLevel(roster);
    if (typeof explicitLevel !== "number" || !(explicitLevel >= 0)) { return byRoster; }
    return Math.min(CROWD_LADDER.length - 1, Math.max(byRoster, Math.floor(explicitLevel)));
}

export function crowdSettingsFor(gameState) {
    var roster = gameState && gameState.players ? gameState.players.length : 0;
    return CROWD_LADDER[resolveCrowdLevel(gameState && gameState.crowdLevel, roster)];
}

// The host's cadence for a roster: the simulation step, and the client push
// interval as a whole number of steps closest to PUSH_TARGET_MS (a push is
// only ever sent on a tick). 15ms steps push every 3 (45ms); 20ms steps every
// 2 (40ms). The client reads the same numbers for its netstats targets.
export var PUSH_TARGET_MS = 45;
// `explicitLevel`: the match's own level when it carries one (resolveCrowdLevel).
export function hostCadence(roster, explicitLevel) {
    var tickMs = CROWD_LADDER[resolveCrowdLevel(explicitLevel, roster)].tickMs;
    var pushMs = tickMs * Math.max(1, Math.round(PUSH_TARGET_MS / tickMs));
    return { tickMs: tickMs, pushMs: pushMs, ticksPerSec: Math.round(1000 / tickMs), pushesPerSec: Math.round(10000 / pushMs) / 10 };
}
